import { isCX } from 'api/DeviceInfo';
import Header from 'components/Headers/Header';
import LoadingBanner from 'components/Misc/LoadingBanner.js';
import NavigationPath from 'components/Misc/NavigationPath';
import { useContext, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardHeader,
  Container,
  Row,
  UncontrolledTooltip,
} from 'reactstrap';
import { GlobalContext } from '../api/GlobalContext';
import Collapsible from '../components/Misc/Collapsible';
import ConfirmationModal from '../components/Misc/ConfirmationModal.js';
import EditableLabel from '../components/Misc/EditableLabel';
import { getFirstEmrNoteType } from '../components/Misc/utils';
import AddSectionModal from '../components/Modals/BuilderSectionNoteModal.js';
import Modal from '../components/Modals/Modal.js';
import { withSavedSubsectionNames } from '../utils/subsectionNames';
import {
  getHaveInjector,
  getPageInfo,
} from '../EMR_Integration/react_webInjector.ts';

const SUPPORTED_EMRS = [
  'Athena Health',
  'Clinicient',
  'Comprehend Health',
  'Cora Health',
  'Empower',
  'Fusion',
  'Heno',
  'Jane',
  'Practice Perfect',
  'Practice Pro',
  'Prompt',
  'PT Everywhere',
  'TheraOffice',
  'WebPT',
];

const TemplateDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { apiClient } = useContext(GlobalContext);

  const queryParams = new URLSearchParams(location.search);
  const configIDFromUrl = queryParams.get('configId');
  const emrFromUrl = queryParams.get('emr');

  const [loading, setLoading] = useState(false);
  const [configID, setConfigID] = useState(configIDFromUrl || -1);
  const [name, setName] = useState('Template Name');
  const [questions, setQuestions] = useState({});
  const [emrUrl, setEmrUrl] = useState('');
  const [noteType, setNoteType] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');
  const [modalHeader, setModalHeader] = useState('PDF Template Builder');
  const [modalButtons, setModalButtons] = useState([]);
  const [expandedSections, setExpandedSections] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [isDev, setIsDev] = useState(false);
  const [addSectionModalOpen, setAddSectionModalOpen] = useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState({
    isOpen: false,
    sectionName: '',
  });
  const [templateReply, setTemplateReply] = useState({});
  const [subsections, setSubsections] = useState({});
  const setTemplateReplyHandler = async (questionKey, newValue) => {
    try {
      // Update local state immediately for responsiveness
      setTemplateReply((prev) => {
        const updated = { ...(prev || {}) };
        updated[questionKey] = newValue;
        return updated;
      });

      // Persist to backend with only the reply string for this section
      await apiClient.devUpdateLibraryConfigReplyType(
        configID,
        emrFromUrl,
        newValue,
        questionKey,
      );
    } catch (e) {
      console.error('Failed to update reply type:', e);
    }
  };

  const setContentHandler = async (questionKey, newContent) => {
    try {
      // Update local state immediately for responsiveness
      setQuestions((prev) => {
        const updated = { ...prev };
        updated[questionKey] = newContent;
        return updated;
      });

      // Update subsections if the new content contains SubSections
      if (typeof newContent === 'string') {
        try {
          const parsed = JSON.parse(newContent);
          if (parsed.SubSections && typeof parsed.SubSections === 'object') {
            setSubsections((prev) => {
              const updated = { ...prev };
              updated[questionKey] = {
                ...(prev[questionKey] || {}),
                ...parsed.SubSections,
              };
              return updated;
            });
          }
        } catch {
          // Not JSON or no SubSections, ignore
        }
      }

      // Persist to backend
      await apiClient.editLibraryConfigInstruction(
        configID,
        emrFromUrl,
        questionKey,
        newContent,
      );
    } catch (e) {
      console.error('Failed to update instruction:', e);
    }
  };
  const getStoredValue = (key, param1, param2) => {
    const path = sessionStorage.getItem(key) || '';
    const params = new URLSearchParams(path.split('?')[1]);
    return params.get(param1) || params.get(param2) || null;
  };

  const selectedBrowseValue = getStoredValue(
    'Library-Browse-Path',
    'emr',
    'note',
  );
  const selectedListValue = getStoredValue('Library-List-Path', 'emr', 'note');

  // Update configID when URL changes
  useEffect(() => {
    setConfigID(configIDFromUrl || -1);
  }, [configIDFromUrl]);

  // Fetch template details when configID changes
  useEffect(() => {
    if (configID === -1) return;
    fetchTemplateDetails();
  }, [configID, emrFromUrl]);

  const fetchTemplateDetails = async () => {
    try {
      const data = await apiClient.getLibraryConfigDetails(
        configID,
        emrFromUrl,
      );
      if (data?.error) {
        console.error('Error getting config details:', data.error);
        return;
      }
      let userDetails = await apiClient.getStats();
      setIsDev(userDetails?.isDev || false);
      setName(data.name || 'Template Name');
      setQuestions(data.questions || {});
      setEmrUrl(data.emr_url);
      setNoteType(getFirstEmrNoteType(data));
      // Initialize per-question reply type map if available
      if (data.reply_type && typeof data.reply_type === 'object') {
        setTemplateReply(data.reply_type);
      } else {
        setTemplateReply({});
      }
      // Extract subsections from questions - subsections are embedded in JSON strings
      const extractedSubsections = {};
      if (data.questions) {
        Object.keys(data.questions).forEach((key) => {
          const questionValue = data.questions[key];
          if (typeof questionValue === 'string') {
            try {
              const parsed = JSON.parse(questionValue);
              if (
                parsed.SubSections &&
                typeof parsed.SubSections === 'object'
              ) {
                extractedSubsections[key] = parsed.SubSections;
              }
            } catch {
              // Not a JSON string, ignore
            }
          } else if (
            typeof questionValue === 'object' &&
            questionValue !== null &&
            questionValue.SubSections
          ) {
            extractedSubsections[key] = questionValue.SubSections;
          }
        });
      }
      setSubsections(withSavedSubsectionNames(extractedSubsections, data.subsectionNames));
      setIsPageLoaded(true);
    } catch (error) {
      console.error('Failed to fetch config details:', error);
    }
  };

  // Effect to expand sections after page is loaded
  useEffect(() => {
    if (isPageLoaded && Object.keys(questions).length > 0) {
      setExpandedSections(true);
    }
  }, [isPageLoaded, questions]);

  /**
   * Handles copying the template and creating a new conversation
   */
  const handleCopy = async () => {
    try {
      setLoading(true);
      const newConfig = await apiClient.copyLibraryConfig(configID, emrFromUrl);

      if (newConfig?.configId && !newConfig.error) {
        const newConvoData = await apiClient.newConversation(
          '',
          newConfig.configId,
        );
        if (newConvoData?.uid) {
          navigate(`/User/conversations/details#${newConvoData.uid}`, {
            state: { name, configId: newConfig.configId },
          });
        } else {
          console.error('Failed to create a new conversation:', newConvoData);
        }
      } else {
        if (newConfig?.error?.includes('You already have a copy')) {
          showModal(
            'Warning!',
            `You already have a copy of this template under 'Note Settings'`,
          );
        } else {
          showModal('Warning!', 'Failed to create a new template');
        }
      }
    } catch (error) {
      console.error('Failed to copy configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Validates EMR compatibility for CX environment
   */
  const handleCXValidation = async () => {
    const haveInjector = await getHaveInjector();
    if (!haveInjector) return;

    const pageInfo = await getPageInfo();
    if (pageInfo['error'] && pageInfo['error'].includes('No Injector Found')) {
      alert(
        'No EMR detected. Please load up your EMR and Template. Try Refreshing',
      );
      return;
    }

    let pageEmrUrl = pageInfo['URL'] || '';
    let pageNoteType = pageInfo['Note Type'] || '';
    if (!isCompatibleEMR(pageEmrUrl, pageNoteType)) {
      const emrName = extractEMRName(pageEmrUrl);
      showIncompatibleEMRModal(emrName, pageNoteType);
    } else {
      await handleCopy();
    }
  };

  const isCompatibleEMR = (pageEmrUrl, pageNoteType) => {
    return pageEmrUrl.toLowerCase().includes(emrUrl.toLowerCase());
    // && pageNoteType.toLowerCase().includes(noteType.toLowerCase());
  };

  const extractEMRName = (pageEmrUrl) => {
    const emrparts = pageEmrUrl.split('.');
    let emrNameFromParts =
      emrparts.length > 2 ? emrparts[emrparts.length - 2] : emrparts[0];
    if (emrNameFromParts === 'mwtherapy') {
      emrNameFromParts = 'Empower';
    }
    return SUPPORTED_EMRS.find((emr) =>
      emrNameFromParts.toLowerCase().includes(emr.toLowerCase()),
    );
  };

  const showIncompatibleEMRModal = (emrName, pageNoteType) => {
    const message =
      emrFromUrl && emrName && noteType && pageNoteType
        ? `This template is only compatible for '${emrFromUrl}'. Please checkout templates specifically for your EMR - '${emrName}'`
        : `Please load up your ${name} EMR and Template. Try Refreshing`;

    const buttons =
      emrFromUrl && emrName
        ? [
            {
              label: `View ${emrName} Templates`,
              color: 'primary',
              onClick: () => {
                setModalOpen(false);
                navigate(
                  `/User/template/library/browse?index=1&emr=${emrName}`,
                );
              },
            },
          ]
        : [];

    showModal('', message, buttons);
  };

  const showModal = (header, text, buttons = []) => {
    setModalHeader(header);
    setModalText(text);
    setModalButtons(buttons);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalButtons([]);
  };

  /**
   * Handles adding a new section to the library template
   */
  const handleAddSection = async (sectionName) => {
    if (!sectionName || !sectionName.trim()) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.addLibraryConfigSection(
        configID,
        emrFromUrl,
        sectionName.trim(),
      );

      if (response?.error) {
        console.error('Error adding section:', response.error);
        showModal('Error', response.error);
        return;
      }

      // Add the section to the local state at the top
      setQuestions((prevQuestions) => {
        const newQuestions = { [sectionName.trim()]: 'New Section' };
        return { ...newQuestions, ...prevQuestions };
      });

      setAddSectionModalOpen(false);
    } catch (error) {
      console.error('Failed to add section:', error);
      showModal('Error', 'Failed to add section. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Opens confirmation modal for section deletion
   */
  const handleDeleteSectionClick = (sectionName) => {
    setConfirmationDetails({
      isOpen: true,
      sectionName: sectionName,
    });
  };

  /**
   * Handles deleting a section from the library template
   */
  const handleDeleteSection = async () => {
    const { sectionName } = confirmationDetails;

    try {
      setLoading(true);
      const response = await apiClient.deleteLibraryConfigSection(
        configID,
        emrFromUrl,
        sectionName,
      );

      if (response?.error) {
        console.error('Error deleting section:', response.error);
        showModal('Error', response.error);
        return;
      }

      // Remove the section from local state
      setQuestions((prevQuestions) => {
        const newQuestions = { ...prevQuestions };
        delete newQuestions[sectionName];
        return newQuestions;
      });

      setConfirmationDetails({ isOpen: false, sectionName: '' });
    } catch (error) {
      console.error('Failed to delete section:', error);
      showModal('Error', 'Failed to delete section. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderQuestionList = () => (
    <DragDropContext>
      <Droppable droppableId="droppable-1">
        {(provided) => (
          <ul
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={{ padding: '20px' }}
          >
            {Object.keys(questions).map((key, index) => (
              <Draggable key={key} draggableId={key} index={index}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                  >
                    <Collapsible
                      label={
                        <EditableLabel
                          text={key}
                          className="h3"
                          disabled={true}
                          removeBorder={true}
                        />
                      }
                      content={questions[key]}
                      setContent={(newContent) =>
                        setContentHandler(key, newContent)
                      }
                      questionKey={key}
                      isTemplate={true}
                      configID={configID}
                      disabled={!isDev}
                      triggerShow={expandedSections}
                      isLibraryConfig={true}
                      deleteButton={
                        isDev ? (
                          <div style={{ margin: '10px' }}>
                            <Button
                              className="fa fa-trash"
                              onClick={() => handleDeleteSectionClick(key)}
                              style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: '#888',
                                fontSize: '16px',
                                cursor: 'pointer',
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.color = '#f76c6c')
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.color = '#888')
                              }
                            />
                          </div>
                        ) : null
                      }
                      isDev={isDev}
                      showSelector={true}
                      templateReply={templateReply || {}}
                      setTemplateReply={setTemplateReplyHandler}
                      subsections={
                        subsections && subsections[key]
                          ? subsections[key]
                          : null
                      }
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
    </DragDropContext>
  );

  const breadcrumbItems = [
    { name: 'Template Library', path: '/User/template/library' },
    {
      name: selectedBrowseValue,
      path: sessionStorage.getItem('Library-Browse-Path'),
    },
    {
      name: selectedListValue,
      path: sessionStorage.getItem('Library-List-Path'),
    },
    { name, path: location.pathname + location.search },
  ];

  return (
    <>
      <LoadingBanner
        showBanner={loading}
        loadingText="Making template yours and starting a new visit..."
      />
      <Header isLite={true} />
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        buttons={modalButtons}
        isCloseButton={false}
      >
        <h2>{modalHeader}</h2>
        <p>{modalText}</p>
      </Modal>
      <AddSectionModal
        isOpen={addSectionModalOpen}
        onClose={() => setAddSectionModalOpen(false)}
        onAdd={handleAddSection}
        type={false}
      />
      <ConfirmationModal
        isOpen={confirmationDetails.isOpen}
        onClose={() =>
          setConfirmationDetails({ isOpen: false, sectionName: '' })
        }
        onConfirm={handleDeleteSection}
        confirmButtonText="Delete"
      >
        Are you sure you want to delete this section?
      </ConfirmationModal>
      <Container className="mt--9" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <NavigationPath paths={breadcrumbItems} />
              <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                <Link
                  to={`/User/templates?tab=${location.state?.tab || 'personal'}`}
                />
                <EditableLabel text={name} disabled={true} className="h3" />
                {isDev && (
                  <>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => setAddSectionModalOpen(true)}
                      style={{ marginRight: '5px' }}
                    >
                      <i
                        className="fas fa-plus"
                        style={{ marginRight: '5px' }}
                      ></i>
                      Add Section
                    </Button>
                    <Button
                      color="danger"
                      size="sm"
                      onClick={async () =>
                        await apiClient.devDemote(configID, emrFromUrl)
                      }
                    >
                      <i
                        className="fas fa-times-circle"
                        style={{ marginRight: '5px' }}
                      ></i>
                      Demote
                    </Button>
                  </>
                )}
                <div>
                  <Button
                    id="useTemplateBtn"
                    color="primary"
                    size="sm"
                    onClick={() =>
                      isCX() ? handleCXValidation() : handleCopy()
                    }
                  >
                    Use it now
                  </Button>
                  {isCX() && (
                    <UncontrolledTooltip
                      placement="top"
                      target="useTemplateBtn"
                      delay={{ show: 200, hide: 0 }}
                    >
                      First Load up your EMR and Note Type
                    </UncontrolledTooltip>
                  )}
                </div>
              </CardHeader>

              <div className="list-container">{renderQuestionList()}</div>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default TemplateDetails;
